<template>
  <base-page-layout title="Pontos">
    <mf-tabs-menu v-model="mainPageTab" :tabs="mainTabs" class="mb-6" />
    <div v-if="mainPageTab === 0">
      <v-row class="my-3">
        <v-col v-for="(card, index) in cardsMetrics" :key="index" cols="3">
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :tooltip="card.helpText"
            :boolean-loader="$apollo.queries.metrics.loading"
            :percent-text="card.underText"
            :percent-description="card.description"
            :is-currency="card.isCurrency"
            :quick-action="card.quickAction"
            unset-percentage-colors
            simple-card
          />
        </v-col>
      </v-row>
      <mf-tabs-menu v-model="offerTypeCurrentTab" :tabs="offersTypeTabs" class="mt-4" />
      <div class="d-flex justify-end my-3">
        <mf-button icon="add" data-cy="newVoucher" label="Novo vale-troca" @click="redirect" />
      </div>
      <index-table v-if="offerTypeCurrentTab === 0" />
      <voucher-type
        v-if="offerTypeCurrentTab === 1"
        key="voucher-tab-1"
        voucher-type="consumed"
        date-name="consumed_at"
        csv-file-name="consumo_de_vales_troca"
        title="Histórico de vales-troca consumidos"
        :filters="['lojas', 'data']"
      />
      <voucher-type
        v-if="offerTypeCurrentTab === 2"
        key="voucher-tab-2"
        voucher-type="activated"
        date-name="activated_at"
        csv-file-name="vales_troca_ativos"
        title="Histórico de vales-troca ativos"
        use-new-filters
        :filters="['lojas']"
      />
      <voucher-type
        v-if="offerTypeCurrentTab === 3"
        key="voucher-tab-3"
        voucher-type="expired"
        date-name="expired_at"
        csv-file-name="vales_troca_expirados"
        title="Histórico de vales-troca expirados"
        use-new-filters
        :filters="['lojas']"
      />
    </div>

    <div v-else-if="mainPageTab === 1">
      <voucher-cpf-block />
    </div>

    <div v-else-if="mainPageTab === 2">
      <points-balance-locks-table />
    </div>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import IndexTable from '../components/index/IndexTable.vue'
import { QUERY_GET_VOUCHERS_METRICS } from '../../graphql'
import { localeString } from '@/config/helpers.js'
import VoucherType from '../components/index/VoucherType.vue'
import VoucherCpfBlock from '../components/index/voucher-cpf-block/VoucherCpfBlock.vue'
import PointsBalanceLocksTable from '../components/PointsBalanceLocksTable.vue'
import { criteriaGroup, customerHasAtLeastOnePointCriteria } from '@/components/quick-actions/segmentationGroups.js'

export default {
  components: {
    BasePageLayout,
    IndexTable,
    CbmCard: () => import('@/components/CbmCard.vue'),
    VoucherType,
    VoucherCpfBlock,
    PointsBalanceLocksTable,
  },
  data: () => ({
    mainPageTab: 0,
    offerTypeCurrentTab: null,
    metrics: {
      total_users_vouchers_activated: '',
      total_users_vouchers_activated_percentage: '',
      total_user_points: '',
      total_user_with_points: '',
      total_vouchers_active: '',
      total_vouchers_active_discount: '',
      total_vouchers_active_product: '',
      total_vouchers_consumed: '',
      total_vouchers_consumed_points: '',
    },
  }),
  computed: {
    mainTabs() {
      return [{ name: 'Pontos' }, { name: 'Controle de CPF/CNPJ' }, { name: 'Bloqueio de Saldos' }]
    },
    cardsMetrics() {
      return [
        {
          text: 'Clientes que ativaram',
          value: localeString(this.metrics?.total_users_vouchers_activated || 0),
          underText: `${localeString(this.metrics?.total_users_vouchers_activated_percentage || 0, 0)}%`,
          description: `dos ${localeString(this.metrics?.total_user_with_points || 0)} que já pontuaram`,
          helpText: 'Total de clientes que já realizaram alguma troca de pontos por vales-troca de desconto.',
        },
        {
          text: 'Pontos na carteira',
          value: localeString(this.metrics?.total_user_points || 0),
          helpText: 'Total de pontos disponíveis agora para troca por vale-troca de desconto.',
          quickAction: {
            id: 'customer-has-points',
            shortDescription: `Clientes que possuem pontos na carteira`,
            longDescription: `Clientes que possuem pontos na carteira`,
            segmentationGroup: () => criteriaGroup(customerHasAtLeastOnePointCriteria()),
            tooltip: 'Este é o total de pontos na carteira. O segmento vai ser de acordo com o número de pessoas que têm pontos.',
          },
        },
        {
          text: 'Vales ativos',
          value: localeString(this.metrics?.total_vouchers_active || 0, 0),
          description: `Desconto: R$ ${localeString(this.metrics?.total_vouchers_active_discount || 0, 0)} | Produtos: ${localeString(
            this.metrics?.total_vouchers_active_product || 0,
            0
          )}`,
          helpText: 'Total de vales que foram ativados e ainda estão disponíveis para uso.',
        },
        {
          text: 'Pontos consumidos',
          value: localeString(this.metrics?.total_vouchers_consumed_points || 0),
          description: `Totalizando ${localeString(this.metrics?.total_vouchers_consumed || 0)} vales-troca`,
          helpText: 'Total de pontos que foram utilizados para resgate de vales-troca.',
        },
      ]
    },
    offersTypeTabs() {
      return [
        { name: 'Vales-troca', offerSalesKeyName: 'vouchers_home' },
        { name: 'Consumo de vales-troca', offerSalesKeyName: 'vouchers_consumption' },
        { name: 'Vales-troca ativos', offerSalesKeyName: 'active_vouchers' },
        { name: 'Vales-troca expirados', offerSalesKeyName: 'expired_vouchers' },
      ]
    },
    hashRoutes() {
      return {
        0: 'vales-troca',
        1: 'consumo-de-vales-troca',
        2: 'vales-troca-ativos',
        3: 'vales-troca-expirados',
      }
    },
  },
  apollo: {
    metrics: {
      query: QUERY_GET_VOUCHERS_METRICS,
      update({ pointsVouchersMetrics }) {
        return pointsVouchersMetrics
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  watch: {
    offerTypeCurrentTab(newValue) {
      if (this.hashRoutes[newValue] !== this.$route.hash.substring(1)) {
        this.$router.push({ hash: this.hashRoutes[newValue] })
      }
    },
    $route(newValue) {
      const { hash } = newValue
      const formattedHash = hash.substring(1)

      if (formattedHash === 'vales-troca') this.offerTypeCurrentTab = 0
      if (formattedHash === 'consumo-de-vales-troca') this.offerTypeCurrentTab = 1
      if (formattedHash === 'vales-troca-ativos') this.offerTypeCurrentTab = 2
      if (formattedHash === 'vales-troca-expirados') this.offerTypeCurrentTab = 3
    },
    mainPageTab(newValue) {
      if (newValue === 0) {
        this.$apollo.queries.metrics.refetch()
      }
    },
  },
  mounted() {
    const {
      hash,
      params: { tab: paramTab },
      query: { tab: queryTab },
    } = this.$route

    const formattedHash = hash.substring(1)

    if (formattedHash.length && Object.values(this.hashRoutes).includes(formattedHash)) {
      const hashIndex = Object.values(this.hashRoutes).findIndex((hashRoute) => hashRoute === formattedHash)
      this.offerTypeCurrentTab = hashIndex
      return
    }
    const tab = queryTab || paramTab

    if (tab) {
      if (tab === 2) {
        this.mainPageTab = 1
      } else {
        this.offerTypeCurrentTab = tab
      }
    } else {
      this.offerTypeCurrentTab = 0
    }

    if (this.mainPageTab === 0) {
      this.$router.push({ hash: this.hashRoutes[this.offerTypeCurrentTab] })
    }
  },
  methods: {
    textVoucher(value) {
      return value === 1 ? 'vale-troca' : 'vales-troca'
    },
    textPonto(value) {
      return value !== 1 ? `Já foram gastos ${localeString(value)} pontos` : `Já foi gasto ${localeString(value)} ponto`
    },
    redirect() {
      this.$router.push('/mobile/pontos/novo').catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-nova {
  margin-top: -54px !important;
}
.text-lower {
  text-transform: none;
}
</style>
